
import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminCreatedUser } from '@/services/userAuthService';
import staffService from '@/services/staffService';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface User {
  id: string;
  username: string;
  fullName: string;
  role: string;
  email: string | null;
  active: boolean;
  permissions?: string[];
}

interface EditUserDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  selectedUser: User | null;
  setSelectedUser: React.Dispatch<React.SetStateAction<User | null>>;
}

const EditUserDialog: React.FC<EditUserDialogProps> = ({ 
  isOpen, 
  onOpenChange, 
  selectedUser, 
  setSelectedUser 
}) => {
  const queryClient = useQueryClient();
  
  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: string, data: Partial<AdminCreatedUser> }) => 
      staffService.updateStaffUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast('User Updated', {
        description: 'The user has been updated successfully.'
      });
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast('Error', {
        description: error.message || 'Failed to update user.',
      });
    }
  });

  const handleUpdateUser = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedUser) {
      updateUserMutation.mutate({
        id: selectedUser.id,
        data: {
          role: selectedUser.role as 'admin' | 'staff' | 'clinic',
          fullName: selectedUser.fullName,
          email: selectedUser.email,
          active: selectedUser.active
        }
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
          <DialogDescription>
            Update user information for {selectedUser?.username}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleUpdateUser}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-role" className="text-right">
                Role
              </Label>
              <Select
                value={selectedUser?.role}
                onValueChange={(value: 'admin' | 'staff' | 'clinic') => 
                  setSelectedUser(prev => prev ? { ...prev, role: value } : null)}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="clinic">Clinic</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-fullName" className="text-right">
                Full Name
              </Label>
              <Input
                id="edit-fullName"
                value={selectedUser?.fullName ?? ''}
                onChange={(e) => setSelectedUser(prev => prev ? { ...prev, fullName: e.target.value } : null)}
                className="col-span-3"
                required
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-email" className="text-right">
                Email
              </Label>
              <Input
                id="edit-email"
                type="email"
                value={selectedUser?.email ?? ''}
                onChange={(e) => setSelectedUser(prev => prev ? { ...prev, email: e.target.value } : null)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-status" className="text-right">
                Status
              </Label>
              <Select
                value={selectedUser?.active ? 'active' : 'inactive'}
                onValueChange={(value) => 
                  setSelectedUser(prev => prev ? { ...prev, active: value === 'active' } : null)}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={updateUserMutation.isPending}>
              {updateUserMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditUserDialog;
